import React from "react";
import { CheckCircle2, User, Building, CreditCard, ShieldCheck } from "lucide-react";
import type { SowFields } from "../../../../../utils/api/endpoints/sow";

interface AcceptanceSignaturesEditorProps {
  fields: SowFields;
  setField: <K extends keyof SowFields>(key: K, value: SowFields[K]) => void;
  clientName: string;
}

export const AcceptanceSignaturesEditor: React.FC<AcceptanceSignaturesEditorProps> = ({
  fields,
  setField,
  clientName
}) => {
  return (
    <div className="space-y-6">
      <div className="border-b border-slate-200 pb-4">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
          <CheckCircle2 size={16} className="text-ctb-blue" />
          Section 10: Acceptance, Signatures & Billing Information
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          Review and confirm client signatory details, official registered company identity, corporate billing data, and CTB execution signers.
        </p>
      </div>

      {/* Client Signer Details */}
      <div className="space-y-3">
        <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
          <User size={13} className="text-ctb-blue" />
          Client Authorised Signatory
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-slate-600">Signer Full Name</label>
            <input
              type="text"
              value={fields.clientSignerName || ""}
              onChange={(e) => setField("clientSignerName", e.target.value)}
              placeholder="e.g. John Doe"
              className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-slate-600">Signer Job Title / Role</label>
            <input
              type="text"
              value={fields.clientSignerTitle || ""}
              onChange={(e) => setField("clientSignerTitle", e.target.value)}
              placeholder="e.g. Chief Technology Officer / Director"
              className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-slate-600">Signer Email</label>
            <input
              type="email"
              value={fields.clientEmail || ""}
              onChange={(e) => setField("clientEmail", e.target.value)}
              placeholder="john@example.com"
              className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-slate-600">Signer Phone</label>
            <input
              type="text"
              value={fields.clientPhone || ""}
              onChange={(e) => setField("clientPhone", e.target.value)}
              placeholder="+64 21 000 0000"
              className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
            />
          </div>
        </div>
      </div>

      {/* Client Billing & Corporate Entity */}
      <div className="space-y-3 pt-2 border-t border-slate-200">
        <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
          <Building size={13} className="text-purple-600" />
          Client Corporate & Billing Entity
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-slate-600">Registered Company Name</label>
            <input
              type="text"
              value={fields.clientRegisteredCompany ?? ""}
              onChange={(e) => setField("clientRegisteredCompany", e.target.value)}
              placeholder="e.g. Acme Corporation Limited"
              className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800 font-medium"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-slate-600">Country of Incorporation</label>
            <input
              type="text"
              value={fields.clientCountryOfIncorporation ?? ""}
              onChange={(e) => setField("clientCountryOfIncorporation", e.target.value)}
              placeholder="e.g. New Zealand"
              className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-slate-600">Invoice Addressed To</label>
            <input
              type="text"
              value={fields.clientBillingName ?? ""}
              onChange={(e) => setField("clientBillingName", e.target.value)}
              placeholder="Accounts Payable / Finance Team"
              className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-slate-600">GST / Tax / Company Number</label>
            <input
              type="text"
              value={fields.clientTaxNumber || ""}
              onChange={(e) => setField("clientTaxNumber", e.target.value)}
              placeholder="e.g. GST: 123-456-789"
              className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
            />
          </div>

          <div className="md:col-span-2 space-y-1">
            <label className="text-[11px] font-semibold text-slate-600">Registered Billing Address</label>
            <input
              type="text"
              value={fields.clientBillingAddress || ""}
              onChange={(e) => setField("clientBillingAddress", e.target.value)}
              placeholder="Full street address, city, postcode, country"
              className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-slate-600">Billing Email</label>
            <input
              type="email"
              value={fields.clientBillingEmail || ""}
              onChange={(e) => setField("clientBillingEmail", e.target.value)}
              placeholder="billing@example.com"
              className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-slate-600">Billing Phone</label>
            <input
              type="text"
              value={fields.clientBillingPhone || ""}
              onChange={(e) => setField("clientBillingPhone", e.target.value)}
              placeholder="+64 9 000 0000"
              className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
            />
          </div>
        </div>
      </div>

      {/* CTB Signer Details */}
      <div className="space-y-3 pt-2 border-t border-slate-200">
        <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
          <ShieldCheck size={13} className="text-emerald-600" />
          Capture The Bug Limited Authorised Signatory
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-slate-600">CTB Signer Name</label>
            <input
              type="text"
              value={fields.ctbSignerName || "Ankita Dhakar"}
              onChange={(e) => setField("ctbSignerName", e.target.value)}
              className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-slate-600">CTB Signer Title</label>
            <input
              type="text"
              value={fields.ctbSignerTitle || "CEO / Head of Cybersecurity"}
              onChange={(e) => setField("ctbSignerTitle", e.target.value)}
              className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-slate-600">CTB Phone</label>
            <input
              type="text"
              value={fields.ctbSignerPhone || "+64-221994320"}
              onChange={(e) => setField("ctbSignerPhone", e.target.value)}
              className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-slate-600">CTB Email</label>
            <input
              type="email"
              value={fields.ctbSignerEmail || "ankita@capturethebug.xyz"}
              onChange={(e) => setField("ctbSignerEmail", e.target.value)}
              className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
